/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import _ from 'underscore';

import ToolbarHelper from './ToolbarHelper';
import LocalFilterDialog from './LocalFilterDialog';

export default class FilterActionHandler {

	/**
	 * FilterActionHandler is a collection of api's that allow easy editing of local filters via a filter dialog
	 *
	 * @param {Object} attributes
	 * @param {DashboardAPI} attributes.dashboard - dashboard API
	 * @param {ContentAPI} attributes.content - content API
	 * @param {Function} [attributes.cbOnRemove] - callback to be called when the editor is removed
	 * @param {Function} attributes.cbGetActionsForFilter - callback to be called to get the actions
	 */
	constructor(attributes) {
		this.dashboard = attributes.dashboard;
		this.content = attributes.content;
		this._cbOnRemove = attributes.cbOnRemove || this._cbOnRemove;
		this._getActions = attributes.cbGetActionsForFilter;

		this.visualization = this.content.getFeature('Visualization');
		this.visDnD = this.content.getFeature('VisDnD');
		this.localFilters = this.visualization.getLocalFilters();
		this.slots = this.visualization.getSlots();

		// TODO: Local filter dialog class needs to be refactored
		this.localFilterDialog = new LocalFilterDialog({
			dashboard: this.dashboard,
			content: this.content
		});

		this._toolbarHelper = new ToolbarHelper();
	}

	getDatasourceId() {
		const dataSource = this.visualization.getDataSource();
		if (dataSource) {
			return dataSource.getId();
		}
		return null;
	}

	/**
	 * editLocalFilters is called to create a dialog in scenarios such as when a data item is dropped in the Context Filters section when editing slots.
	 * @param {Object} droppedColumns - the structure contains information of the columns dropped in the local filter slot (MetadataDnDResults object)
	 * @param {Object} localFiltersDomNode - a domNode which is used to place the editor.
	 * @param {Object} editorOptions - further options
	 *			1) placement - placement overrides (eg auto)
	 *			2) forceOpen - when false, a filter entry can be replaced without launching the UI.
	*/
	editLocalFilters(droppedColumns, localFiltersDomNode, editorOptions) {

		let metadataColumns = _.map(droppedColumns && droppedColumns.columns, (column) => column.metadataColumn);

		if (metadataColumns.length > 0) {
			const transactionApi = this.dashboard.getFeature('Transaction');
			const transactionToken = transactionApi.startTransaction();

			const firstMetadataColumn = metadataColumns[0];
			const sourceId = firstMetadataColumn.getSourceId();
			if(sourceId) {
				this.visualization.setDataSource(sourceId, transactionToken);
			}

			// add columns of member type first
			const columnsWithMembers = droppedColumns.utils.getColumnsWithMembers(true /* filterMemberColumns */);
			const columnIds = Object.keys(columnsWithMembers);
			if (columnIds.length) {
				this.content.getFeature('VisDnD.utils').addMembersAsLocalFilters({ visualizationAPI: this.visualization, columns: droppedColumns.columns }, transactionToken);
				// remove these columns from metadataColumns
				metadataColumns = metadataColumns.filter(column => !columnIds.includes(column.getId()));
			}

			// then add columns of type model filters
			const modelFilters = metadataColumns.filter(column => column.isFilter());
			if (modelFilters.length) {
				const modelFilterIds = modelFilters.map(column => column.getId());
				this._addModelFilters(modelFilterIds, transactionToken);
				metadataColumns = metadataColumns.filter(column => !modelFilterIds.includes(column.getId()));
			}
			// then display filter dialog to add filters for columns of type query item
			if (metadataColumns.length) {
				// we don't support for now multiple rendering of filter dialog, so just render dialog of the 1st query item
				const column = metadataColumns[0];
				this.localFilterDialog.renderDialog(column.getId(), localFiltersDomNode, editorOptions);
			}
			transactionApi.endTransaction(transactionToken);
		}
	}

	insertItemFromTypeInSlot(metadataColumn, sourceId, options) {
		const localFilterSelector = '.slotEditor__localFilters';
		const itemId = metadataColumn.getId();
		const localFiltersDomNode = options.parentNode || document.querySelector(localFilterSelector);
		const transactionApi = this.dashboard.getFeature('Transaction');
		const transactionToken = transactionApi.startTransaction();
		if(sourceId) {
			this.visualization.setDataSource(sourceId, transactionToken);
		}
		const defaultEditorOptions = {
			placement: 'bottom',
			attachPoint: localFilterSelector + '__attachPoint'
		};
		if (metadataColumn.isFilter()) {
			this._addModelFilters([itemId], transactionToken);
		} else {
			const editorOptions = options.editorOptions || defaultEditorOptions;
			this.localFilterDialog.renderDialog(itemId, localFiltersDomNode, editorOptions);
		}
		transactionApi.endTransaction(transactionToken);
	}

	/**
	 * This method adds dropped columns of type "model filter" in the local filters
	 * @param modelFilterIds - list of model filter identifiers (metadataColumn.getId()) to add in local filters
	 * @param transactionToken - transaction token for the action
	*/
	_addModelFilters(modelFilterIds, transactionToken) {
		const localFilters = this.visualization.getLocalFilters();
		modelFilterIds.forEach(filterId => {
			const itemContext = {
				id: filterId,
				itemId: filterId,
				filterType: 'filter'
			};
			localFilters.addFilter(itemContext, {} /* filterContext */, transactionToken);
		});
	}

	getContentId() {
		return this.content.getId();
	}

	/**
	 * @returns the actions associated with this metadataColumn and filterId
	 */
	getActions(metadataColumn, filterId, itemId) {
		return this._getActions(metadataColumn, filterId, itemId);
	}

	openFilterItemActionMenu(filterInfo, parentNode, editorOptions) {
		const dataSource = this.visualization.getDataSource();
		if(!dataSource){
			return Promise.resolve([]);
		}
		const metadataColumn = dataSource.getMetadataColumn(filterInfo.columnId);
		const filterId = filterInfo.filterId;
		return this.getActions(metadataColumn, filterId, filterInfo.columnId)
			.then((actions) => {
				let aActions = [];
				_.each(actions, (action) => {
					aActions = aActions.concat(action.getAvailableActions());
				});

				aActions.sort((a, b) => {
					return a.order - b.order;
				});

				this._toolbarHelper.buildActionToolbar(aActions, parentNode, /*label*/null, editorOptions);
				return actions;
			});
	}

	/* check if action menu is open */
	isFilterActionMenuOpened() {
		return this._toolbarHelper.getToolbar() ? true : false;
	}

	getDropTargetInfo(node) {
		let dropTargetInfo = {
			type: 'slot.filter',
			info: {
				contentId: this.getContentId(),
				node: node,
				slotDataItemHandler: this
			}
		};
		return dropTargetInfo;
	}

	onDropA11Y(node) {
		const target = this.getDropTargetInfo(node);
		this.visDnD.onDrop({ type: 'A11YMetadataColumns' }, target);
	}

	getPickTreeContext() {
		return {
			type: 'localfilters'
		};
	}
}



// WEBPACK FOOTER //
// ./src/js/FilterActionHandler.js